// src/app/features/inspection-dashboard/inspection-dashboard.component.ts
import {Component, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {MatPaginator} from '@angular/material/paginator';
import {DashboardFilterOptions, InspectionService} from "../services/inspection.service";
import {LoaderService} from "../../shared/services/loader.service";
import {EquipmentPointModel} from "../../../models/equipment-point.model";
import {
    InspectionDetailsDialogComponent
} from "../inspection-results-details/dialog/inspection-details-dialog/inspection-details-dialog.component";
import {MatDialog} from "@angular/material/dialog";
import {InspectionModel} from "../../../models/inspection.model";
import {SiteUnityFilters} from "../../shared/components/site-unity-filters/site-unity-filters.component";
import {takeUntil} from "rxjs/operators";
import {Subject} from "rxjs";

interface FilterOption {
    code: number | null;
    translationKey: string;
    checked: boolean;
}

@Component({
    selector: 'app-inspection-dashboard',
    templateUrl: './inspection-dashboard.component.html',
    styleUrls: ['./inspection-dashboard.component.scss']
})
export class InspectionDashboardComponent implements OnInit, OnDestroy {
    private destroy$ = new Subject<void>();
    @ViewChild(MatPaginator) paginator!: MatPaginator;

    inspections: InspectionModel[] = [];
    totalItems = 0;
    currentPage = 1;
    itemsPerPage = 10;

    filterOptions: FilterOption[] = [
        {code: null, translationKey: 'common.all', checked: true},
        {code: 1, translationKey: 'ffsRecommendationCode.1', checked: true},
        {code: 2, translationKey: 'ffsRecommendationCode.2', checked: true},
        {code: 3, translationKey: 'ffsRecommendationCode.3', checked: true},
        {code: 4, translationKey: 'ffsRecommendationCode.4', checked: true},
    ];

    currentFilters: SiteUnityFilters = {
        selectedSites: [],
        selectedUnities: []
    };

    constructor(
        private inspectionService: InspectionService,
        private readonly loaderService: LoaderService,
        private dialog: MatDialog
    ) {
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    ngOnInit(): void {
        this.loadInspections();
    }

    loadInspections(): void {
        const options: DashboardFilterOptions = {
            page: this.currentPage || 1,
            limit: this.itemsPerPage,
            ffsRecommendationCodes: this.getSelectedFilterCodes(),
            siteIds: this.currentFilters.selectedSites.length > 0 ? this.currentFilters.selectedSites : undefined,
            unityIds: this.currentFilters.selectedUnities.length > 0 ? this.currentFilters.selectedUnities : undefined
        };
        this.loaderService.showLoader();
        this.inspectionService.getInspectionsDashboard(options)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: (response) => {
                    this.inspections = response.data;
                    this.totalItems = response.total;
                },
                error: (error) => {
                    console.error('Erreur lors du chargement des inspections', error);
                },
                complete: () => {
                    this.loaderService.hideLoader();
                }
            });
    }

    getSelectedFilterCodes(): number[] {
        return this.filterOptions
            .filter((option, index) => index > 0 && option.checked)
            .map(option => option.code)
            .filter((code): code is number => code !== null);
    }

    onFilterChange(isAllOption: boolean, _: number): void {
        if (isAllOption) {
            // Option "Toutes" changée, appliquer son état aux autres options
            const allChecked = this.filterOptions[0].checked;
            this.filterOptions.forEach(option => option.checked = allChecked);
        } else {
            // Autre option changée, mettre à jour l'option "Toutes"
            const allChecked = this.filterOptions
                .filter((_, i) => i > 0)
                .every(option => option.checked);

            this.filterOptions[0].checked = allChecked;
        }

        this.resetToFirstPage();
        this.loadInspections();
    }

    onSiteUnityFiltersChange(filters: SiteUnityFilters): void {
        this.currentFilters = filters;
        this.resetToFirstPage();
        this.loadInspections();
    }

    private resetToFirstPage(): void {
        if (this.paginator) {
            this.paginator.pageIndex = 0;
        }
        this.currentPage = 1;
    }

    onPageChange(state: { page: number; pageOffset: number }) {
        this.currentPage = state.page;
        this.itemsPerPage = state.pageOffset;
        this.loadInspections();
    }

    getRecommendationClass(code: number): string {
        if (code === undefined) {
            return '';
        }
        switch (code) {
            case 1:
                return 'do-rbi';
            case 2:
                return 'consider-inspection';
            case 3:
                return 'further-inspection';
            case 4:
                return 'full-inspection';
            default:
                return '';
        }
    }

    getRecommendationTranslationKey(recommendationCode: number): string {
        return `ffsRecommendationCode.${recommendationCode}`;
    }

    openInspectionDetails(inspection: any): void {
        const equipmentPoint: EquipmentPointModel = inspection.equipmentPoint;

        this.dialog.open(InspectionDetailsDialogComponent, {
            width: '70%',
            maxWidth: '1200px',
            height: '98%',
            panelClass: 'inspection-details-dialog',
            data: {point: equipmentPoint}
        });
    }
}
